import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LockKeyhole } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/contexts/LangContext';

export default function ResetPasswordPage() {
  const { resetPassword } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const { search } = useLocation();
  const token = new URLSearchParams(search).get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!token) {
      toast.error(t('auth.reset_missing_token'));
      return;
    }
    if (!password || !confirmPassword) {
      toast.error(t('alerts.required_fields'));
      return;
    }
    if (password !== confirmPassword) {
      toast.error(t('auth.passwords_do_not_match'));
      return;
    }
    setLoading(true);
    try {
      await resetPassword({ token, password, confirm_password: confirmPassword });
      toast.success(t('auth.reset_success'));
      setTimeout(() => navigate('/login'), 800);
    } catch (err) {
      toast.error(err.response?.data?.error || t('alerts.generic_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-12">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-lg">
        <Card className="rounded-2xl shadow-sm">
          <CardContent className="p-8 sm:p-10">
            <div className="h-12 w-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-6">
              <LockKeyhole className="h-6 w-6" />
            </div>
            <h1 className="text-2xl font-semibold mb-2">{t('auth.reset_password')}</h1>
            <p className="text-sm text-muted-foreground">{t('auth.reset_password_desc')}</p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div>
                <Label className="text-sm font-medium mb-1.5">{t('auth.new_password')}</Label>
                <Input
                  type="password"
                  className="rounded-lg"
                  placeholder={t('auth.new_password')}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5">{t('auth.confirm_password')}</Label>
                <Input
                  type="password"
                  className="rounded-lg"
                  placeholder={t('auth.confirm_password')}
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                />
              </div>
              <Button type="submit" className="w-full rounded-xl h-11" disabled={loading || !token}>
                {loading ? t('common.loading') : t('auth.reset_password')}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm text-muted-foreground">
              <Link to="/login" className="text-primary font-medium hover:underline">
                {t('verify.back_login')}
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
